import "./customers.css";
import useUserContext from "../../hooks/useUserContext";
import {ColProps, colTypeOptions, FetchProps, FilterProps, filterTypeOptions} from "../../components/table/tableTypes";
import {exportToCsv} from "../../utils/exportToCsv";
import {formatDate} from "../../utils/friendlyDate";
import Table from "../../components/table/Table";
import {getFullName} from "../../utils/getFullName";
import {RoundFullCheckIcon} from "../../assets/icons/RoundFullCheckIcon";
import {getCustomersReport} from "../../api/getCustomersReport";
import {MenuProps, Switch, Tooltip} from "antd";
import BlockCustomerSwitch from "./BlockCustomerSwitch";

const Customers = () => {
    const { selectedDomain } = useUserContext();

    const fetch = async ({ filters, sortingBy, sortingDirection, limit, offset, selectedDomain, isCsvCall, page, pageSize }: FetchProps) => {
        if(isCsvCall) {
            const response = await getCustomersReport(selectedDomain, filters);
            console.log("response.data.report: ", response.data.report);
            const csvReport = response.data.report.map((row: any) => ({
                "ID": row.shopClientId,
                "Customer": getFullName(row.firstName, row.lastName),
                "Phone": row.phone,
                "Email": row.mail,
                "General Subscription": row.emailConsent ? 'YES' : 'NO',
                "SMS Subscription": row.smsConsent ? 'YES' : 'NO',
                "Total Checkouts": row.totalCheckouts,
                "Total Messages Delivered": row.totalMessagesDelivered,
                "Total Clicks": row.totalClicks,
                "Total Orders": row.totalOrders,
                "Opted Out": row.optedOut ? 'YES' : 'NO',
                "Blocked": row.blocked ? 'YES' : 'NO',
            }));
            exportToCsv(csvReport, `Customers - ${selectedDomain} - ${formatDate(new Date())}`);
        } else {
            const response = await getCustomersReport(selectedDomain, filters, page, pageSize);
            return response;
        }
    }

    const filterLocally = (rows: any[], filters: FilterProps[]) => {
        const customerNameFilter = filters.find(filter => filter.filterKey === 'customerName');
        let filteredResponse = customerNameFilter?.value ? rows.filter((customer: any) => getFullName(customer.firstName, customer.lastName).toLowerCase().includes((customerNameFilter.value as string).toLowerCase())) : rows;

        const phoneFilter = filters.find(filter => filter.filterKey === 'phone');
        filteredResponse = phoneFilter?.value ? filteredResponse.filter((customer: any) => customer.phone?.includes((phoneFilter.value as string).toLowerCase())) : filteredResponse;

        const subscriptionFilter = filters.find(filter => filter.filterKey === 'subscription');
        filteredResponse = subscriptionFilter?.selected ? filteredResponse.filter((customer: any) => {
            console.log("subscriptionFilter?.selected: ", subscriptionFilter?.selected);
            if(subscriptionFilter.selected === 'general' && customer.emailConsent) return true;
            else if(subscriptionFilter.selected === 'sms' && customer.smsConsent) return true;
            else if(subscriptionFilter.selected === 'allSubscriptions' && customer.emailConsent && customer.smsConsent) return true;
            return false;
        }) : filteredResponse;

        const optedOutFilters = filters.find(filter => filter.filterKey === 'optedOut');
        filteredResponse = optedOutFilters?.selected ? filteredResponse.filter((customer: any) => {
            if(optedOutFilters.selected === 'optedOut' && customer.optedOut) return true;
            else if(optedOutFilters.selected === 'notOptedOut' && !customer.optedOut) return true;
            return false;
        }) : filteredResponse;

        const blockedFilters = filters.find(filter => filter.filterKey === 'blocked');
        filteredResponse = blockedFilters?.selected ? filteredResponse.filter((customer: any) => {
            if(blockedFilters.selected === 'blocked' && customer.blocked) return true;
            else if(blockedFilters.selected === 'notBlocked' && !customer.blocked) return true;
            return false;
        }) : filteredResponse;

        return filteredResponse;
    }

    const cols: ColProps[] = [
        // { key: 'shopClientId', name: 'ID', type: colTypeOptions.text, width: 80, isPinned: true },
        { key: 'customer', name: 'Customer', type: colTypeOptions.text, width: 130, isPinned: true, render: (value, row) => getFullName(row.firstName?.replace(/\b\w/g, (c: any) => c.toUpperCase()), row.lastName?.replace(/\b\w/g, (c: any) => c.toUpperCase())) },
        // { key: 'email', name: 'Email Consent', type: colTypeOptions.text, width: 130, render: (value, row) => <div className="vertical-centered"> </div> },
        { key: 'phone', name: 'SMS Consent', type: colTypeOptions.text, width: 170, render: (value, row) => <div className="vertical-centered">
                <div className="flex gap-0">
                    {row.emailConsent ?
                        <Tooltip title="General Subscription"><div><RoundFullCheckIcon width={18} color='#8a5de7' /></div></Tooltip> :
                        <Tooltip title="No General Subscription"><div><RoundFullCheckIcon width={18} color='#f3f3f3' /></div></Tooltip>
                    }
                    {row.smsConsent ?
                        <Tooltip title="SMS Subscription"><div><RoundFullCheckIcon width={18} color='#8a5de7' /></div></Tooltip> :
                        <Tooltip title="No SMS Subscription"><div><RoundFullCheckIcon width={18} color='#f3f3f3' /></div></Tooltip>
                    }
                </div>
                <div className="flex1">{value}</div></div>
        },
        { key: 'totalCheckouts', name: 'Checkouts', type: colTypeOptions.number, width: 100 },
        { key: 'totalMessagesDelivered', name: 'Messages', type: colTypeOptions.number, width: 100 },
        { key: 'totalClicks', name: 'Clicks', type: colTypeOptions.number, width: 100 },
        { key: 'totalOrders', name: 'Orders', type: colTypeOptions.number, width: 100 },
        { key: 'optedOut', name: 'Opted Out', type: colTypeOptions.text, width: 100, render: (value) => <div>{value ? 'Yes' : 'No'}</div> },
        { key: 'blocked', name: 'Blocked', type: colTypeOptions.text, width: 100, render: (value, row) => <BlockCustomerSwitch key={`customer-blocked-${row.id}`} initialState={value} clientId={row.shopClientId} /> },
    ];

    const subscriptionOptions: MenuProps['items'] = [
        {key: 'general', label: 'General Subscribers'},
        {key: 'sms', label: 'SMS Subscribers'},
        {key: 'allSubscriptions', label: 'General & SMS Subscribers'},
    ];

    const optedOutOptions: MenuProps['items'] = [
        {key: 'optedOut', label: 'Opted Out'},
        {key: 'notOptedOut', label: 'Receive Messages'},
    ];

    const blockedOptions: MenuProps['items'] = [
        {key: 'blocked', label: 'Blocked customers'},
        {key: 'notBlocked', label: 'Not Blocked'},
    ];

    const filters: FilterProps[] = [
        { filterKey: 'customerName', name: 'Customer', type: filterTypeOptions.text },
        { filterKey: 'phone', name: 'Phone Number', type: filterTypeOptions.text },
        { filterKey: 'subscription', name: 'Subscription', type: filterTypeOptions.textToKey, options: subscriptionOptions, allLabel: 'All Customers' },
        { filterKey: 'optedOut', name: 'Opted-Out Customers', type: filterTypeOptions.textToKey, options: optedOutOptions, allLabel: 'All Customers' },
        { filterKey: 'blocked', name: 'Blocked Customers', type: filterTypeOptions.textToKey, options: blockedOptions, allLabel: 'All Customers' },
    ];

    return (
        <div className="reports-container">
            <Table
                title="Customers"
                fetch={fetch} cols={cols} filters={filters} showSearch={false} doneLoadingDependencies={true} />
        </div>
    );
    //
    // return (
    //     <Page
    //         className="shop-clients-container"
    //         title="Customers"
    //         // subtitle="This is the list of your customers"
    //         // primaryAction={{
    //         //     label: 'Add Customer',
    //         //     action: () => setIsNewCustomerModalOpen(true),
    //         //     isDisabled: !isEnabled,
    //         // }}
    //     >
    //         customers...
    //     </Page>
    // )
}

export default Customers;